export interface ContactPathSeed {
  showContactPhone?: boolean;
  phoneNumber?: string;
  showContactUrl?: boolean;
  inquiryUrl?: string;
}

export interface ContactSeedTarget {
  showContactPhone: boolean;
  phoneNumber: string;
  showContactUrl: boolean;
  contactUrl: string;
}

/**
 * Fills only empty contact fields. A chat hint never replaces a value already
 * stored on the inquiry page or the phone number.
 */
export function applyContactSeed(
  current: ContactSeedTarget,
  seed: ContactPathSeed,
): ContactSeedTarget {
  const next: ContactSeedTarget = { ...current };
  const seededPhone = typeof seed.phoneNumber === 'string' ? seed.phoneNumber.trim() : '';
  if (seededPhone && !current.phoneNumber.trim()) {
    next.phoneNumber = seededPhone;
    next.showContactPhone = true;
  }
  const seededUrl = typeof seed.inquiryUrl === 'string' ? seed.inquiryUrl.trim() : '';
  if (seededUrl && !current.contactUrl.trim()) {
    next.contactUrl = seededUrl;
    next.showContactUrl = true;
  }
  return next;
}

export function extractContactHints(text: string): ContactPathSeed {
  const hints: ContactPathSeed = {};
  const urlMatch = text.match(/https?:\/\/[^\s]+/i);
  if (urlMatch) {
    hints.showContactUrl = true;
    hints.inquiryUrl = urlMatch[0].replace(/[)）,.]+$/, '');
  }
  const phoneMatch = text.match(/(?:0\d{1,4}[-ー−]\d{1,4}[-ー−]\d{3,4}|0\d{9,10})/);
  if (phoneMatch) {
    hints.showContactPhone = true;
    hints.phoneNumber = phoneMatch[0];
  }
  return hints;
}

const CONTACT_PATH_INTENT = [
  /問い?合わ?せ/,
  /お問合/,
  /連絡先/,
  /電話/,
  /コンタクト/,
  /窓口/,
  /\bcontact\b/i,
  /\binquir/i,
  /\bphone\b/i,
];

/** The admin is talking about the contact path, not about a knowledge source. */
export function hasContactPathIntent(text: string): boolean {
  if (typeof text !== 'string') return false;
  return CONTACT_PATH_INTENT.some((pattern) => pattern.test(text));
}

/**
 * Hints for a message the admin typed themselves. A bare URL is ambiguous - it is
 * far more often a crawl target or a host site - so drafting it as the inquiry
 * link needs contact wording in the same message. A phone number is unambiguous.
 */
export function contactHintsFromUserMessage(text: string): ContactPathSeed {
  const hints = extractContactHints(text);
  if (hints.inquiryUrl && !hasContactPathIntent(text)) {
    delete hints.inquiryUrl;
    delete hints.showContactUrl;
  }
  return hints;
}
